import React from 'react';
import { Button, Col, FormGroup, Input, Label, Row, Spinner, Progress } from 'reactstrap';
import { toast } from 'react-toastify';
import {
  ModuleUploadForm,
  PITCH_NOTES,
  PRACTICE_MODULE_TYPES,
} from './practiceTypes';
import { uploadPracticeModule } from './practiceApi';

type Props = {
  folderId: number;
  folderPath: string;
  practiceName: string;
  createdBy: string;
  onDone: () => void;
  onBack: () => void;
};

const emptyForm: ModuleUploadForm = {
  name: '',
  description: '',
  pitch: '',
  tempo: '',
  isEnabledForAI: true,
  moduleType: '',
};

const ModuleUpload: React.FC<Props> = ({
  folderId,
  folderPath,
  practiceName,
  createdBy,
  onDone,
  onBack,
}) => {
  const [form, setForm] = React.useState<ModuleUploadForm>(emptyForm);
  const [file, setFile] = React.useState<File | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [phase, setPhase] = React.useState<'idle' | 'uploading' | 'creating'>('idle');
  const [progress, setProgress] = React.useState(0);

  const set = <K extends keyof ModuleUploadForm>(key: K, value: ModuleUploadForm[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const validate = (): string | null => {
    if (!form.name.trim()) return 'Name is required.';
    if (!form.moduleType) return 'Practice Module Type is required.';
    if (!file) return 'Please choose a file to upload.';
    return null;
  };

  const handleUpload = async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setBusy(true);
    setPhase('uploading');
    setProgress(0);
    try {
      await uploadPracticeModule({
        folderId,
        folderPath,
        folderName: practiceName,
        form,
        file: file as File,
        createdBy,
        onProgress: (p) => {
          setProgress(p);
          if (p >= 100) setPhase('creating');
        },
      });
      toast.success('Module uploaded.');
      onDone();
    } catch (e: any) {
      console.error('Module upload failed:', e?.response?.status, e?.response?.data, e);
      const detail =
        e?.response?.data?.message ||
        e?.response?.statusText ||
        e?.message ||
        'Unknown error';
      toast.error(`Upload failed: ${detail}`);
    } finally {
      setBusy(false);
      setPhase('idle');
    }
  };

  // Upload form.
  return (
    <div className="practice-form">
      <p className="practice-form__context">
        New module for <strong>{practiceName}</strong>
      </p>

      <Row>
        <Col md={6}>
          <FormGroup>
            <Label>Name *</Label>
            <Input
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="Module name"
            />
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label>Practice Module Type *</Label>
            <Input
              type="select"
              value={form.moduleType}
              onChange={(e) => set('moduleType', e.target.value as any)}
            >
              <option value="">Select…</option>
              {PRACTICE_MODULE_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Input>
          </FormGroup>
        </Col>
      </Row>

      <FormGroup>
        <Label>Description</Label>
        <Input
          type="textarea"
          value={form.description}
          onChange={(e) => set('description', e.target.value)}
          placeholder="Short description"
        />
      </FormGroup>

      <Row>
        <Col md={6}>
          <FormGroup>
            <Label>Pitch</Label>
            <Input
              type="select"
              value={form.pitch}
              onChange={(e) => set('pitch', e.target.value as any)}
            >
              <option value="">Select…</option>
              {PITCH_NOTES.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </Input>
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label>Tempo (BPM)</Label>
            <Input
              type="number"
              min={0}
              value={form.tempo}
              onChange={(e) =>
                set('tempo', e.target.value === '' ? '' : Number(e.target.value))
              }
              placeholder="e.g. 120"
            />
          </FormGroup>
        </Col>
      </Row>

      <FormGroup switch className="practice-form__switch">
        <Input
          type="switch"
          checked={form.isEnabledForAI}
          onChange={(e) => set('isEnabledForAI', e.target.checked)}
        />
        <Label check>Enabled for AI</Label>
      </FormGroup>

      <FormGroup>
        <Label>Upload File *</Label>
        <Input
          type="file"
          accept="audio/*,video/*"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
        {file && <small className="text-muted">Selected: {file.name}</small>}
      </FormGroup>

      {busy && (
        <div className="practice-form__progress">
          {phase === 'uploading' ? (
            <>
              <div className="practice-form__progress-row">
                <span>Uploading audio…</span>
                <span>{progress}%</span>
              </div>
              <Progress value={progress} />
            </>
          ) : (
            <div className="practice-form__progress-row">
              <Spinner size="sm" /> <span>Saving module…</span>
            </div>
          )}
        </div>
      )}

      <div className="practice-form__actions">
        <Button color="secondary" outline onClick={onBack} disabled={busy}>
          Cancel
        </Button>
        <Button color="primary" onClick={handleUpload} disabled={busy}>
          {busy
            ? phase === 'uploading'
              ? `Uploading ${progress}%`
              : 'Saving…'
            : 'Upload'}
        </Button>
      </div>
    </div>
  );
};

export default ModuleUpload;
